/**
 * 任务项接口定义
 */
interface TaskItem<T> {
    /** 任务唯一标识符 */
    id: string;
    /** 任务执行函数 */
    fn: () => Promise<T>;
    /** Promise resolve 回调函数 */
    resolve: (value: T | PromiseLike<T>) => void;
    /** Promise reject 回调函数 */
    reject: (reason?: any) => void;
}

/**
 * 任务队列管理器
 * 用于管理和控制异步任务的并发执行
 * @template T 任务执行结果的类型
 */
export default class TaskQueueManager<T> {
    /** 最大并发数 */
    private readonly maxConcurrent: number;
    /** 等待执行的任务队列 */
    private waitingQueue: TaskItem<T>[];
    /** 当前正在执行的任务数量 */
    private runningCount: number;
    /** 存储任务执行结果的 Map */
    private results: Map<string, T | { error: any }>;
    /** 正在执行的任务 ID 集合 */
    private runningTasks: Set<string>;
    /** 记录任务开始时间的 Map */
    private taskStartTimes: Map<string, number>;
    // 新增任务检查Map
    private pendingTasks: Map<string, boolean> = new Map();

    /**
     * 构造函数
     * @param maxConcurrent 最大并发数，默认为 6
     */
    constructor(maxConcurrent: number = 6) {
        this.maxConcurrent = maxConcurrent;
        this.waitingQueue = [];
        this.runningCount = 0;
        this.results = new Map();
        this.runningTasks = new Set();
        this.taskStartTimes = new Map();
    }

    /**
     * 获取格式化的时间字符串
     * @returns 格式化的时间字符串 (HH:mm:ss)
     */
    private getTimeString(): string {
        return new Date().toISOString().slice(11, 19);  // 使用 slice 替代 substr
    }

    /**
     * 添加新任务到队列
     * @param taskId 任务唯一标识符
     * @param requestFn 任务执行函数
     * @returns Promise，resolve 时返回任务执行结果
     */
    public addTask(taskId: string, requestFn: () => Promise<T>): Promise<T> {
        if (this.pendingTasks.has(taskId)) {
            return Promise.reject('任务已存在');
        }
        this.pendingTasks.set(taskId, true);

        console.log(`${this.getTimeString()} [队列] 添加新任务: ${taskId}`);
        return new Promise<T>((resolve, reject) => {
            const taskItem: TaskItem<T> = {
                id: taskId,
                fn: requestFn,
                resolve,
                reject
            };

            this.waitingQueue.push(taskItem);
            this.processQueue();
        });
    }

    /**
     * 处理队列中的待执行任务
     * 当有等待中的任务且未达到最大并发数时，继续执行新的任务
     */
    private processQueue(): void {
        if (this.waitingQueue.length > 0 || this.runningCount > 0) {
            console.log(`\n${this.getTimeString()} [状态] 运行中: ${this.runningCount}/${this.maxConcurrent}, 等待中: ${this.waitingQueue.length}`);
            if (this.runningCount > 0) {
                console.log('[并发执行中]:', Array.from(this.runningTasks).join(', '));
            }
        }

        while (this.waitingQueue.length > 0 && this.runningCount < this.maxConcurrent) {
            const task = this.waitingQueue.shift()!;
            this.executeTask(task);
        }
    }

    /**
     * 执行单个任务
     * @param task 要执行的任务项
     */
    private async executeTask(task: TaskItem<T>): Promise<void> {
        this.runningCount++;
        this.runningTasks.add(task.id);
        this.taskStartTimes.set(task.id, Date.now());

        console.log(`${this.getTimeString()} [开始] ${task.id}`);

        try {
            const result = await task.fn();
            const duration = Date.now() - this.taskStartTimes.get(task.id)!;
            console.log(`${this.getTimeString()} [完成] ${task.id} (耗时: ${duration}ms)`);
            // this.results.set(task.id, result);
            task.resolve(result);
        } catch (error) {
            console.log(`${this.getTimeString()} [失败] ${task.id}`);
            // this.results.set(task.id, { error });
            task.reject(error);
        } finally {
            this.runningCount--;
            this.runningTasks.delete(task.id);
            this.taskStartTimes.delete(task.id);
            this.pendingTasks.delete(task.id); // 清理任务
            this.processQueue();
        }
    }

    /**
     * 清空任务队列和相关状态
     */
    public clear(): void {
        this.waitingQueue = [];
        this.results.clear();
        this.runningTasks.clear();
        this.taskStartTimes.clear();
        console.log('[系统] 已清空任务队列');
    }
}